import { config } from "./common/constants";
import { getCSRFToken, storeTokens, TMessage } from "./auth-helper";
import { getTeams } from "./apiclient";
import MixpanelTracker from "./common/mixpanel-tracker";

type AuthFlow = "login" | "logout";
export type RefreshTrigger = "taskpane_load" | "window_focus" | "api_unauthorized";

export function logout() {
  MixpanelTracker.trackEvent("logout_started", {});

  // const realm = localStorage.getItem("realm") || "fundwave";

  const token = sessionStorage.getItem("token");
  const redirectUri = new URL(`${window.location.origin}/logout-redirect.html`);
  const logoutUri = new URL(`${window.location.origin}/auth-logout.html`);
  logoutUri.searchParams.set("redirectUri", redirectUri.toString());
  if (token) logoutUri.searchParams.set("id_token_hint", token);

  openPopup(logoutUri, "logout");
}

export function login() {
  MixpanelTracker.trackEvent("login_started", {});

  const baseUrl = window.location.origin;
  // const csrfToken = getCSRFToken();
  // sessionStorage.setItem("csrf-token", csrfToken);

  const redirectUri = new URL(`${baseUrl}/login-redirect.html`);
  // redirectUri.searchParams.set("state", config.openIdBaseUrl);

  const serviceUserUrl = new URL(`${baseUrl}/auth-login.html`);
  serviceUserUrl.searchParams.set("redirectUri", redirectUri.toString());

  openPopup(serviceUserUrl, "login");
}

export function openPopup(popupUrl: URL, flow: AuthFlow) {
  if (Office.context.ui !== undefined) {
    Office.context.ui.displayDialogAsync(
      popupUrl.toString(),
      { height: 85, width: 40 },

      function (asyncResult) {
        const dialog = asyncResult.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => processMessage(arg, flow, dialog));
      }
    );
  } else {
    const dialog = window.open(popupUrl, "AuthPopup", "scrollbars=yes,width=660,height=500,centerscreen=1");

    const messageHandler = (arg: MessageEvent) => {
      window.removeEventListener("message", messageHandler);
      processMessage(arg, flow, dialog);
    };

    window.addEventListener("message", messageHandler);
  }
}

async function processMessage(arg: any, flow: AuthFlow, dialog?: Office.Dialog | Window | undefined | null) {
  try {
    const data: TMessage = JSON.parse(Office.context.ui !== undefined ? arg.message : arg.data);

    if (data.authType === "logout") {
      MixpanelTracker.trackEvent("logout_success", {});
      clearStorage("logout");
      returnToTaskpane();
      return;
    }

    // const validLoginAttempt = validateCsrfToken(data.state);
    // if (!validLoginAttempt) return;

    const isUserVerified = await verifyUser(data.token, flow);
    if (isUserVerified) {
      storeTokens(data);
      MixpanelTracker.trackEvent("login_success", { hasRefreshToken: !!data.refreshToken });
    }
  } catch (error) {
    console.error(`Error while process-message, ${error}`);
    MixpanelTracker.trackEvent(`${flow}_failed`, { reason: "process_message_error", message: String(error).slice(0, 200) });
  } finally {
    dialog?.close();
  }
}

function validateCsrfToken(state?: string) {
  const csrfToken = sessionStorage?.getItem("csrf-token");

  if (!Boolean(state) || !Boolean(csrfToken)) return returnToTaskpane("error", "missing-state");

  const [_, expiryMS] = atob(csrfToken!)?.split(":");
  if (state !== csrfToken || !Boolean(expiryMS)) return returnToTaskpane("error", "invalid-state");

  const now = Date.now();
  if (parseInt(expiryMS) < now) return returnToTaskpane("error", "expired-state");

  sessionStorage.removeItem("csrf-token");
  return true;
}

export async function returnToTaskpane(param?: string, value?: string) {
  const taskpaneUrl = new URL(window.location.origin);
  taskpaneUrl.pathname = "/taskpane.html";

  if (param && value) taskpaneUrl.searchParams.set(param, value);

  window.location.assign(taskpaneUrl);
  window.location.reload();
  return false;
}

async function verifyUser(token: string | undefined, flow: AuthFlow) {
  try {
    const userTeams = await getTeams(token);
    if (!userTeams?.length) {
      MixpanelTracker.trackEvent(`${flow}_failed`, { reason: "no-team" });
      return returnToTaskpane("error", "no-team");
    }

    const userDomains = userTeams.map((userTeam: Record<string, string>) => `https://${userTeam.domain}`);
    const team = userDomains[0];
    localStorage.setItem("team", team);
    localStorage.setItem("domains", JSON.stringify(userDomains));
    returnToTaskpane();

    return true;
  } catch (error) {
    console.error(`Error while verifying user, ${error}`);
    MixpanelTracker.trackEvent(`${flow}_failed`, { reason: "invalidteam", message: String(error).slice(0, 200) });
    return returnToTaskpane("error", "invalidteam");
  }
}

//Verify token
export async function attemptTokenRefresh(trigger: RefreshTrigger) {
  const team = localStorage.getItem("team");
  const realm = process.env.VITE_SSO_REALM || "fundwave";
  localStorage.setItem("realm", realm);
  const token = sessionStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");

  if (!refreshToken || !team) {
    MixpanelTracker.trackEvent("token_refresh_failed", { reason: !refreshToken ? "no_refresh_token" : "no_team", trigger, hadIdToken: !!token });
    return false;
  }

  const tokenRefreshURL = `${config.openIdBaseUrl}/token/refresh`;

  const headers = {
    "Accept": "application/json",
    "Content-Type": "application/json"
  };

  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (refreshToken) headers["Refresh-Token"] = refreshToken;

  try {
    const res = await fetch(tokenRefreshURL, {
      headers
    });

    if (!res.ok) {
      console.error(new Error(`Token refresh failed with status: ${res.status}`));
      MixpanelTracker.trackEvent("token_refresh_failed", { reason: "http_error", trigger, hadIdToken: !!token, httpStatus: res.status });
      return false;
    }

    const data = await res.json();
    const newToken = data["token"];
    const newRefreshToken = data["refreshToken"];
    if (newToken) sessionStorage.setItem("token", newToken);
    if (newRefreshToken) localStorage.setItem("refreshToken", newRefreshToken);

    if (!newToken) {
      MixpanelTracker.trackEvent("token_refresh_failed", { reason: "no_token_in_response", trigger, hadIdToken: !!token });
      return false;
    }

    return true;
  } catch (err: any) {
    console.error(err);
    MixpanelTracker.trackEvent("token_refresh_failed", { reason: "network_error", trigger, hadIdToken: !!token, errorName: err?.name, errorMessage: String(err?.message).slice(0, 200) });
    return false;
  }
}

export function clearStorage(reason: string) {
  const hadSession = ["refreshToken", "team", "useremail"].some((key) => localStorage.getItem(key)) || !!sessionStorage.getItem("token");

  MixpanelTracker.trackEvent("cleared_storage", { reason, hadSession });

  const fetchQueue = (window as any).fetchQueue;
  if (fetchQueue) {
    const pendingRequests = fetchQueue.getQueueLength();
    fetchQueue.emptyQueue();
    MixpanelTracker.trackEvent("queue_cleared", { reason, pendingRequests });
  }

  localStorage.removeItem("useremail");
  localStorage.removeItem("username");
  localStorage.removeItem("domains");
  localStorage.removeItem("team");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("pipelineId");

  sessionStorage.removeItem("token");
  sessionStorage.removeItem("csrf-token");
}
